import { DndContext, DragOverlay, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { isApiError } from '@/api';
import { ConfirmModal } from '@/components';
import { Sidepanel } from '@/components/layout';
import { PageContentCard } from '@/components/layout/PageContentCard';
import { useDeviceModels } from '@/features/device-models/hooks/useDeviceModels';
import { useCurrentCompany } from '@/hooks/useCurrentCompany';
import { useInfiniteScrollSentinel } from '@/hooks/useInfiniteScrollSentinel';
import useDebounce from '@/utils/useDebounce';
import { useLocation } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { File, Files, Folder } from 'lucide-react';
import { useDocumentPermission } from '../context/DocumentPermissionContext';
import { useFolderAncestors, useInfiniteDocumentContents } from '../hooks';
import type { DocumentBreadcrumbItem, DocumentFile } from '../types';
import { CreateFileSidebar } from './CreateFileSidebar';
import { CreateFolderForm } from './partials/CreateFolderForm';
import { DocumentBreadcrumb } from './partials/DocumentBreadcrumb';
import { OsDropOverlay } from './OsDropOverlay';
import { DocumentExplorerContent } from './document-explorer/DocumentExplorerContent';
import type { DocumentExplorerProps } from './document-explorer/DocumentExplorer.types';
import { DocumentExplorerToolbar } from './partials/DocumentExplorerToolbar';
import { FolderPickerDialog } from './FolderPickerDialog';
import { UploadProgressIndicator } from './UploadProgressIndicator';
import { useDocumentNavigation } from './document-explorer/useDocumentNavigation';
import { useFileManagement } from './document-explorer/useFileManagement';
import { useFolderManagement } from './document-explorer/useFolderManagement';
import { useDragAndDrop } from './document-explorer/useDragAndDrop';
import { useOsFileDrop } from './document-explorer/useOsFileDrop';
import { useMultiselect } from './document-explorer/useMultiselect';
import type { SelectableItem } from './document-explorer/useMultiselect';
import { useBulkOperations } from './document-explorer/useBulkOperations';
import { FileDetailPanel } from './fileDetailPanel';
import { newUUID } from '@/utils/uuid';

/**
 * Main document explorer view. Renders the folder/file tree for the given
 * folderId (or root when omitted), handles navigation, search, CRUD actions
 * for folders and files, drag-and-drop moves, OS file drops, and multiselect
 * bulk operations.
 */
export function DocumentExplorer({ folderId }: DocumentExplorerProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const { companyId } = useCurrentCompany();
  const { permission } = useDocumentPermission();
  const { data: deviceModels = [] } = useDeviceModels();
  const isAdmin = permission === 'admin';

  const [searchInput, setSearchInput] = useState('');
  const [selectedFileState, setSelectedFileState] = useState<{ file: DocumentFile; scopeKey: string } | null>(null);
  const [createFileSidebarSession, setCreateFileSidebarSession] = useState({
    isOpen: false,
    sessionId: null as string | null,
    initialFiles: [] as File[],
  });

  // Optimistic removal: items hidden from the list while an API call is in-flight.
  // Stored as a Set of item IDs.
  const [optimisticallyRemovedIds, setOptimisticallyRemovedIds] = useState<ReadonlySet<string>>(new Set());

  const addOptimisticRemovals = useCallback((ids: string[]) => {
    setOptimisticallyRemovedIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return next;
    });
  }, []);

  const revertOptimisticRemovals = useCallback((ids: string[]) => {
    setOptimisticallyRemovedIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.delete(id));
      return next;
    });
  }, []);

  // Bulk delete / move dialog state
  const [isBulkDeleteDialogOpen, setIsBulkDeleteDialogOpen] = useState(false);
  const [isBulkMoveDialogOpen, setIsBulkMoveDialogOpen] = useState(false);
  // Pending DnD move — set on drop, cleared on confirm or cancel
  const [pendingDndMove, setPendingDndMove] = useState<{ items: SelectableItem[]; targetFolderId: string | null } | null>(null);

  const debouncedSearch = useDebounce(searchInput);
  const trimmedSearch = debouncedSearch.trim();

  const locationState = isValidLocationState(location.state) ? location.state : undefined;
  const storedBreadcrumbs = useMemo(
    () => locationState?.documentBreadcrumbs ?? [],
    [locationState]
  );
  const hasStoredBreadcrumbs = Boolean(
    folderId && storedBreadcrumbs.some((crumb) => crumb.id === folderId)
  );

  const contentsQuery = useInfiniteDocumentContents(companyId, folderId, {
    search: trimmedSearch || undefined,
  });
  const ancestorsQuery = useFolderAncestors(folderId, Boolean(folderId) && !hasStoredBreadcrumbs);

  const { folders: rawFolders, files: rawFiles } = useMemo(() => {
    const pages = contentsQuery.data?.pages ?? [];
    return {
      folders: pages.flatMap((page) => page.folders),
      files: pages.flatMap((page) => page.files),
    };
  }, [contentsQuery.data]);

  // Filter out optimistically removed items so the list updates immediately on drop
  const folders = useMemo(
    () => rawFolders.filter((f) => !optimisticallyRemovedIds.has(f.id)),
    [rawFolders, optimisticallyRemovedIds]
  );
  const files = useMemo(
    () => rawFiles.filter((f) => !optimisticallyRemovedIds.has(f.id)),
    [rawFiles, optimisticallyRemovedIds]
  );

  const deviceModelNames = useMemo(
    () => new Map(deviceModels.map((deviceModel) => [deviceModel.id, deviceModel.name])),
    [deviceModels]
  );

  const breadcrumbItems = useMemo(() => {
    if (!folderId) return [];
    if (hasStoredBreadcrumbs) return storedBreadcrumbs;

    const ancestors = ancestorsQuery.data ?? [];
    const ancestorsAlreadyIncludeCurrentFolder = ancestors.some((a) => a.id === folderId);
    if (ancestorsAlreadyIncludeCurrentFolder) return ancestors;

    const firstPage = contentsQuery.data?.pages[0];
    const currentFolderFromContents = firstPage?.currentFolder ?? null;
    const currentFolderIsInContents = currentFolderFromContents?.id === folderId;

    if (currentFolderIsInContents) return [...ancestors, currentFolderFromContents];

    if (contentsQuery.isSuccess) {
      return [
        ...ancestors,
        {
          id: folderId,
          name: currentFolderFromContents?.name ?? t('documents.breadcrumb.currentFolderFallback'),
        },
      ];
    }

    return ancestors;
  }, [ancestorsQuery.data, contentsQuery.isSuccess, contentsQuery.data, folderId, hasStoredBreadcrumbs, storedBreadcrumbs, t]);

  const currentFolder = useMemo(() => {
    if (!folderId) return null;
    const lastBreadcrumb = breadcrumbItems[breadcrumbItems.length - 1];
    const currentFolderName = lastBreadcrumb?.id === folderId ? lastBreadcrumb.name : '';
    return { id: folderId, name: currentFolderName };
  }, [breadcrumbItems, folderId]);

  // Resolves the display name for the drop target in the DnD confirmation dialog.
  // Checks folders in the current view first, then breadcrumb ancestors.
  // A null targetFolderId means root.
  const dndTargetFolderName = useMemo(() => {
    if (!pendingDndMove) return '';
    if (pendingDndMove.targetFolderId === null) return t('documents.title');
    return (
      folders.find((f) => f.id === pendingDndMove.targetFolderId)?.name ??
      breadcrumbItems.find((b) => b.id === pendingDndMove.targetFolderId)?.name ??
      ''
    );
  }, [pendingDndMove, folders, breadcrumbItems, t]);

  const hasItems = folders.length > 0 || files.length > 0;
  const isInitialLoading = contentsQuery.isLoading;
  const isRefreshingList = contentsQuery.isFetching && !contentsQuery.isFetchingNextPage && !isInitialLoading;
  const { fetchNextPage, hasNextPage, isFetchingNextPage } = contentsQuery;
  const selectionScopeKey = `${folderId ?? 'root'}::${trimmedSearch}`;
  const selectedFile = selectedFileState?.scopeKey === selectionScopeKey ? selectedFileState.file : null;
  const isNotFoundError = Boolean(
    folderId && contentsQuery.isError && isApiError(contentsQuery.error) && contentsQuery.error.status === 404
  );

  const navigation = useDocumentNavigation({
    folderId,
    breadcrumbItems,
    isNotFoundError,
    onSelectionClear: () => setSelectedFileState(null),
  });

  // Multiselect
  const multiselect = useMultiselect();

  // Clear multiselect and optimistic removals when navigating to a different folder.
  // Optimistic removals are scoped to the folder the move was initiated from — if we
  // carry them into the target folder they would incorrectly filter out the just-moved
  // items from the fresh server response.
  useEffect(() => {
    multiselect.clearSelection();
    setOptimisticallyRemovedIds(new Set());
    // Only fire on folder navigation, not on every multiselect/setter change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folderId]);

  // Bulk operations (parallel move / delete)
  const bulkOps = useBulkOperations({
    addOptimisticRemovals,
    revertOptimisticRemovals,
    clearSelection: multiselect.clearSelection,
    onFilesDeleted: (deletedFileIds) => {
      // Close the detail panel if the currently open file was deleted
      if (selectedFile && deletedFileIds.includes(selectedFile.id)) {
        setSelectedFileState(null);
      }
    },
  });

  const fileManagement = useFileManagement({
    selectedFileState,
    handleDeleteFiles: bulkOps.executeBulkDelete,
    handleMoveFiles: bulkOps.executeBulkMove,
    invalidateContents: contentsQuery.refetch,
    setSelectedFileState,
  });

  const folderManagement = useFolderManagement({
    companyId,
    folderId,
    trimmedSearch,
    breadcrumbItems,
    folderActions: {
      isDeletingFolders: bulkOps.isBulkDeleting,
      isMovingFolders: bulkOps.isBulkMoving,
      deleteFolders: bulkOps.executeBulkDelete,
      moveFolders: bulkOps.executeBulkMove,
    },
    onNavigateToParent: (parentFolder, parentBreadcrumbs) =>
      navigation.navigateToBreadcrumbItem(parentFolder as DocumentBreadcrumbItem, parentBreadcrumbs.length - 1),
    onNavigateToRoot: navigation.navigateToRoot,
  });


  // Drag-and-drop — selection is passed in so multi-item drags delegate to bulk move
  const dnd = useDragAndDrop({
    onDragStart: () => { },
    onDragEnd: () => { },
    selectedItems: multiselect.selectedItems,
    isItemSelected: multiselect.isSelected,
    onMultiDrop: (items, targetFolderId) => {
      setPendingDndMove({ items, targetFolderId });
    },
  });

  // Use delay sensor so accidental clicks don't trigger drags
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } })
  );

  // OS file drop
  const openCreateFileSidebar = useCallback((initialFiles: File[] = []) => {
    setCreateFileSidebarSession({ isOpen: true, sessionId: newUUID(), initialFiles });
  }, []);

  const closeCreateFileSidebar = useCallback(() => {
    setCreateFileSidebarSession((current) => ({ ...current, isOpen: false }));
  }, []);

  const osFileDrop = useOsFileDrop({
    enabled: isAdmin,
    onFilesDrop: openCreateFileSidebar,
  });

  /** Closes the upload sidebar and opens the file detail panel for the failed file. */
  const handleRetryUpload = useCallback(
    (file: DocumentFile) => {
      setCreateFileSidebarSession((current) => ({ ...current, isOpen: false }));
      setSelectedFileState({ file, scopeKey: selectionScopeKey });
    },
    [selectionScopeKey]
  );

  // Trigger the next page load when the bottom sentinel scrolls into view
  const sentinelRef = useInfiniteScrollSentinel({ hasNextPage, isFetchingNextPage, fetchNextPage });

  return (
    <>
      <DndContext
        sensors={sensors}
        onDragStart={dnd.handleDragStart}
        onDragEnd={dnd.handleDragEnd}
      >
        <PageContentCard className="relative h-full w-full grow flex flex-col text-onbackground">
          <div className="relative flex flex-col gap-4">
            <div className="flex-row gap-3">
              <DocumentExplorerToolbar
                searchInput={searchInput}
                onSearchChange={setSearchInput}
                isAdmin={isAdmin}
                onNewFolder={folderManagement.openCreateFolderForm}
                onNewFile={() => openCreateFileSidebar()}
              />
            </div>

            {isAdmin && folderManagement.isCreateFolderFormOpen ? (
              <CreateFolderForm
                inputRef={folderManagement.createFolderInputRef}
                value={folderManagement.createFolderName}
                error={folderManagement.createFolderError}
                isSubmitting={folderManagement.createFolderMutation.isPending}
                onChange={folderManagement.handleCreateFolderNameChange}
                onKeyDown={folderManagement.handleCreateFolderKeyDown}
                onSubmit={() => void folderManagement.handleCreateFolderSubmit()}
                onCancel={folderManagement.closeCreateFolderForm}
              />
            ) : null}
          </div>

          {/* OS file drop zone wraps the content area */}
          <div ref={osFileDrop.dropZoneRef} className="relative grow flex flex-col overflow-hidden">
            <OsDropOverlay isVisible={osFileDrop.isOsDragOver} />

            <DocumentExplorerContent
              isNotFoundError={isNotFoundError}
              isInitialLoading={isInitialLoading}
              isError={contentsQuery.isError}
              isRefreshingList={isRefreshingList}
              hasItems={hasItems}
              isAdmin={isAdmin}
              trimmedSearch={trimmedSearch}
              folders={folders}
              files={files}
              permission={permission}
              deviceModelNames={deviceModelNames}
              editingFolderId={folderManagement.editingFolderId}
              editingFolderName={folderManagement.editingFolderName}
              isRenamingFolder={folderManagement.renameFolderMutation.isPending}
              isFetchingNextPage={isFetchingNextPage}
              sentinelRef={sentinelRef}
              onFolderClick={navigation.handleFolderClick}
              onFileClick={(file) => {
                if (!isAdmin) return
                setSelectedFileState({ file, scopeKey: selectionScopeKey })
              }}
              onFolderAction={folderManagement.handleFolderAction}
              onFolderRenameStart={folderManagement.beginFolderRename}
              onFolderRenameChange={folderManagement.setEditingFolderName}
              onFolderRenameSubmit={folderManagement.handleFolderRenameSubmit}
              onFolderRenameCancel={folderManagement.cancelFolderRename}
              onFileAction={fileManagement.handleFileAction}
              onRetry={() => {
                void contentsQuery.refetch();
                void ancestorsQuery.refetch();
              }}
              isDndActive={dnd.isDragging}
              draggedItem={dnd.draggedItem}
              isSelected={multiselect.isSelected}
              onToggleSelect={multiselect.toggleItem}
              selectedCount={multiselect.selectedItems.length}
              isBulkMoving={bulkOps.isBulkMoving}
              isBulkDeleting={bulkOps.isBulkDeleting}
              onBulkMoveClick={() => setIsBulkMoveDialogOpen(true)}
              onBulkDeleteClick={() => setIsBulkDeleteDialogOpen(true)}
            />
          </div>

          <DocumentBreadcrumb
            rootLabel={t('documents.title')}
            items={breadcrumbItems}
            onRootClick={() => navigation.navigateToRoot()}
            onItemClick={navigation.navigateToBreadcrumbItem}
            isDndActive={dnd.isDragging}
          />
        </PageContentCard>

        {/* Ghost preview shown while dragging — shows all selected items count for multi-drag */}
        <DragOverlay>
          {dnd.draggedItem ? (
            <div className="bg-surface border-border shadow-md flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium opacity-90">
              {dnd.isDraggingSelection ? (
                <>
                  <Files className="text-primary size-4 shrink-0" />
                  <span className="text-onsurface">
                    {t('documents.multiselect.selectedCount', { count: multiselect.selectedItems.length })}
                  </span>
                </>
              ) : (
                <>
                  {dnd.draggedItem.type === 'folder' ? (
                    <Folder className="text-primary size-4 shrink-0" />
                  ) : (
                    <File className="text-primary size-4 shrink-0" />
                  )}
                  <span className="text-onsurface max-w-[200px] truncate">
                    {dnd.draggedItem.item.name}
                  </span>
                </>
              )}
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      <Sidepanel
        isOpen={Boolean(selectedFile)}
        onClose={() => setSelectedFileState(null)}
        title={selectedFile?.name || t('documents.detail.title')}
        className="sm:w-[70vw] sm:min-w-[440px]"
      >
        <FileDetailPanel
          file={selectedFile}
          permission={permission}
          onFileRemoved={fileManagement.handleFileRemoved}
          onFileUpdated={fileManagement.handleFileUpdated}
        />
      </Sidepanel>

      <CreateFileSidebar
        isOpen={createFileSidebarSession.isOpen}
        sessionId={createFileSidebarSession.sessionId}
        initialFiles={createFileSidebarSession.initialFiles}
        initialFolder={currentFolder}
        currentSearch={trimmedSearch || undefined}
        onClose={closeCreateFileSidebar}
        onRetryUpload={handleRetryUpload}
      />

      {/* Single-item folder delete (dropdown action) */}
      <ConfirmModal
        isOpen={Boolean(folderManagement.folderPendingDelete)}
        title={t('documents.deleteDialog.title', { name: folderManagement.folderPendingDelete?.name ?? '' })}
        message={t('documents.deleteDialog.message', { name: folderManagement.folderPendingDelete?.name ?? '' })}
        confirmLabel={t('documents.deleteDialog.confirm')}
        cancelLabel={t('documents.deleteDialog.cancel')}
        onConfirm={() => void folderManagement.handleFolderDeleteConfirm()}
        onCancel={() => folderManagement.setFolderPendingDelete(null)}
        isLoading={folderManagement.isDeletingFolders}
        variant="danger"
      />

      {/* Single-item folder move (dropdown action) */}
      <FolderPickerDialog
        key={`${folderManagement.folderPendingMove?.id ?? 'none'}:${folderManagement.folderPendingMove?.parentId ?? 'root'}:${Boolean(folderManagement.folderPendingMove)}`}
        open={Boolean(folderManagement.folderPendingMove)}
        excludeFolderId={folderManagement.folderPendingMove?.id}
        initialFolderId={folderManagement.folderPendingMove?.parentId ?? null}
        isSubmitting={folderManagement.isMovingFolders}
        onSelect={folderManagement.handleFolderMoveConfirm}
        onCancel={() => folderManagement.setFolderPendingMove(null)}
      />

      {/* Single-item file delete (dropdown action) */}
      <ConfirmModal
        isOpen={Boolean(fileManagement.filePendingDelete)}
        title={t('documents.detail.deleteFileConfirmTitle', { name: fileManagement.filePendingDelete?.name ?? '' })}
        message={t('documents.detail.deleteFileConfirmMessage')}
        confirmLabel={t('documents.detail.deleteFile')}
        cancelLabel={t('documents.deleteDialog.cancel')}
        onConfirm={() => void fileManagement.handleFileDeleteConfirm()}
        onCancel={() => fileManagement.setFilePendingDelete(null)}
        isLoading={bulkOps.isBulkDeleting}
        variant="danger"
      />

      {/* Single-item file move (dropdown action) */}
      <FolderPickerDialog
        key={`file-move:${fileManagement.filePendingMove?.id ?? 'none'}:${Boolean(fileManagement.filePendingMove)}`}
        open={Boolean(fileManagement.filePendingMove)}
        initialFolderId={fileManagement.filePendingMove?.folderId ?? null}
        isSubmitting={bulkOps.isBulkMoving}
        onSelect={(targetFolder) => void fileManagement.handleFileMoveSelect(targetFolder.id)}
        onCancel={() => fileManagement.setFilePendingMove(null)}
      />

      {/* Multiselect bulk move */}
      <FolderPickerDialog
        key={`bulk-move:${isBulkMoveDialogOpen}`}
        open={isBulkMoveDialogOpen}
        isSubmitting={bulkOps.isBulkMoving}
        onSelect={(targetFolder) => {
          setIsBulkMoveDialogOpen(false);
          void bulkOps.executeBulkMove(multiselect.selectedItems, targetFolder.id);
        }}
        onCancel={() => setIsBulkMoveDialogOpen(false)}
      />

      {/* Multiselect bulk delete */}
      <ConfirmModal
        isOpen={isBulkDeleteDialogOpen}
        title={t('documents.multiselect.bulkDeleteTitle', { count: multiselect.selectedItems.length })}
        message={t('documents.multiselect.bulkDeleteMessage')}
        confirmLabel={t('documents.multiselect.bulkDeleteConfirm')}
        cancelLabel={t('documents.multiselect.bulkDeleteCancel')}
        onConfirm={() => {
          setIsBulkDeleteDialogOpen(false);
          void bulkOps.executeBulkDelete(multiselect.selectedItems);
        }}
        onCancel={() => setIsBulkDeleteDialogOpen(false)}
        isLoading={bulkOps.isBulkDeleting}
        variant="danger"
      />

      {/* DnD move confirmation */}
      <ConfirmModal
        isOpen={Boolean(pendingDndMove)}
        title={t('documents.dnd.confirmMoveTitle', { count: pendingDndMove?.items.length ?? 0 })}
        message={t('documents.dnd.confirmMoveMessage', { folderName: dndTargetFolderName })}
        confirmLabel={t('documents.dnd.confirmMoveConfirm')}
        cancelLabel={t('documents.multiselect.bulkDeleteCancel')}
        onConfirm={() => {
          if (!pendingDndMove) return;
          void bulkOps.executeBulkMove(pendingDndMove.items, pendingDndMove.targetFolderId);
          setPendingDndMove(null);
        }}
        onCancel={() => setPendingDndMove(null)}
        isLoading={bulkOps.isBulkMoving}
      />

      <UploadProgressIndicator />
    </>
  );
}

/**
 * Guards the router location state to ensure it carries a valid breadcrumb
 * array before we use it for navigation state restoration.
 */
function isValidLocationState(
  state: unknown
): state is { documentBreadcrumbs?: DocumentBreadcrumbItem[] } {
  if (state === null || typeof state !== 'object') return false;
  const obj = state as Record<string, unknown>;
  if (!('documentBreadcrumbs' in obj)) return true;
  if (!Array.isArray(obj.documentBreadcrumbs)) return false;
  return obj.documentBreadcrumbs.every(
    (item) =>
      typeof item === 'object' &&
      item !== null &&
      typeof (item as Record<string, unknown>).id === 'string' &&
      typeof (item as Record<string, unknown>).name === 'string'
  );
}
